import React, { useEffect, useRef, useState } from 'react';
import { ArrowBack, Layers, LocalFireDepartment } from "@mui/icons-material";
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import HeatmapLayer from '../../components/GeoDraw/HeatmapLayer';

// CRA/Webpack não resolve os ícones padrão do Leaflet direito (bug
// conhecido do pacote) — não usamos marcador padrão nenhum aqui (só
// Polygon/Polyline/CircleMarker), mas isso evita um ícone quebrado
// aparecer no console caso algo interno do Leaflet tente usar o default.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const CENTRO_PADRAO: [number, number] = [-22.9, -48.4]; // interior de SP, região onde os projetos-piloto atuam

// GeoJSON usa [longitude, latitude]; Leaflet usa [latitude, longitude].
const paraLatLng = (coord: number[]): [number, number] => [coord[1], coord[0]];

const ROTULO_TIPO: Record<string, string> = {
  bacia: 'Bacia',
  rio: 'Rio',
  represa: 'Represa',
  trecho_municipio: 'Trecho município',
  area_pesca: 'Área de pesca',
  outro: 'Outro',
};

const ROTULO_PRECISAO: Record<string, string> = {
  exato: 'Exato (GPS)',
  aproximado: 'Aproximado (rio + município)',
};

const CORES_TIPO: Record<string, string> = {
  bacia: '#1565c0',
  rio: '#00838f',
  represa: '#5e35b1',
  trecho_municipio: '#e65100',
  area_pesca: '#2e7d32',
  outro: '#666',
};

const MapaLocais = () => {
  const [nos, setNos] = useState<any[]>([]);
  const [registros, setRegistros] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [projetos, setProjetos] = useState<any[]>([]);
  const [filtroProjeto, setFiltroProjeto] = useState('');
  const [filtroDataDe, setFiltroDataDe] = useState('');
  const [filtroDataAte, setFiltroDataAte] = useState('');

  const [buscaLocal, setBuscaLocal] = useState('');
  const [resultadosLocal, setResultadosLocal] = useState<any[]>([]);
  const [localSelecionado, setLocalSelecionado] = useState<any>(null);
  const buscaLocalDebounce = useRef<any>(null);

  // Alternância de visualização — puramente de apresentação, não refaz a
  // busca nem toca nos filtros: os mesmos `nos`/`registros` já carregados
  // é que mudam de layer. É por isso que a troca é instantânea e preserva
  // filtro/contexto geográfico atual sem precisar recarregar nada.
  const [modoVisualizacao, setModoVisualizacao] = useState<'padrao' | 'calor'>('padrao');
  // Camadas da legenda: ligar/desligar categorias de nó e de registro sem
  // alterar dado nenhum — só o que é desenhado muda (item 1 da evolução do mapa).
  const [camadasTipos, setCamadasTipos] = useState<string[]>(Object.keys(CORES_TIPO));
  const [camadasPrecisao, setCamadasPrecisao] = useState<string[]>(['exato', 'aproximado']);
  const alternarCamada = (setLista: React.Dispatch<React.SetStateAction<string[]>>, item: string) =>
    setLista((prev) => (prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]));
  const registrosVisiveis = registros.filter((r) =>
    camadasPrecisao.includes(r.precisao_local === 'exato' ? 'exato' : 'aproximado')
  );

  const [buscaMunicipio, setBuscaMunicipio] = useState('');
  const [resultadosMunicipio, setResultadosMunicipio] = useState<any[]>([]);
  const [municipioSelecionado, setMunicipioSelecionado] = useState<any>(null);
  const buscaMunicipioDebounce = useRef<any>(null);

  useEffect(() => {
    api.get('/pesca/projetos').then((res) => setProjetos(res.data.data));
  }, []);

  const fetchMapa = () => {
    setCarregando(true);
    setErro('');
    const params = new URLSearchParams();
    if (filtroProjeto) params.set('uid_pesca_projeto', filtroProjeto);
    if (filtroDataDe) params.set('data_inicio_de', filtroDataDe);
    if (filtroDataAte) params.set('data_inicio_ate', filtroDataAte);
    if (localSelecionado) params.set('uid_pesca_local', localSelecionado.uid_pesca_local);
    if (municipioSelecionado) params.set('id_municipio', String(municipioSelecionado.id_municipio));

    api.get(`/pesca/locais/mapa?${params.toString()}`)
      .then((res) => {
        setNos(res.data.data.nos);
        setRegistros(res.data.data.registros);
      })
      .catch((err) => setErro(err?.response?.data?.message || 'Não foi possível carregar o mapa.'))
      .finally(() => setCarregando(false));
  };

  useEffect(fetchMapa, [filtroProjeto, filtroDataDe, filtroDataAte, localSelecionado, municipioSelecionado]);

  const buscarLocal = (texto: string) => {
    setBuscaLocal(texto);
    setLocalSelecionado(null);
    if (buscaLocalDebounce.current) clearTimeout(buscaLocalDebounce.current);
    if (texto.trim().length < 2) {
      setResultadosLocal([]);
      return;
    }
    buscaLocalDebounce.current = setTimeout(() => {
      api.get(`/pesca/locais?search=${encodeURIComponent(texto.trim())}`).then((res) => setResultadosLocal(res.data.data));
    }, 400);
  };

  const buscarMunicipio = (texto: string) => {
    setBuscaMunicipio(texto);
    setMunicipioSelecionado(null);
    if (buscaMunicipioDebounce.current) clearTimeout(buscaMunicipioDebounce.current);
    if (texto.trim().length < 2) {
      setResultadosMunicipio([]);
      return;
    }
    buscaMunicipioDebounce.current = setTimeout(async () => {
      const res = await api.get(`/pesca/municipios?search=${encodeURIComponent(texto.trim())}`);
      setResultadosMunicipio(res.data.data);
    }, 400);
  };

  const limparFiltros = () => {
    setFiltroProjeto('');
    setFiltroDataDe('');
    setFiltroDataAte('');
    setBuscaLocal('');
    setLocalSelecionado(null);
    setBuscaMunicipio('');
    setMunicipioSelecionado(null);
  };

  const qtdFiltrosAtivos = [filtroProjeto, filtroDataDe, filtroDataAte, localSelecionado, municipioSelecionado].filter(Boolean).length;

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Mapa de Pesca</h1>
        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.85rem', color: '#666', marginRight: '10px' }}>
            {registros.length} registro(s) no mapa
          </p>

          {/* Alterna a representação sem sair da tela — mesmos dados e
              filtros, só troca a layer desenhada por cima do mapa. */}
          <div style={{ display: 'flex', gap: '4px', background: '#f0f0f0', borderRadius: '20px', padding: '3px', marginRight: '6px' }}>
            <button
              type="button"
              title="Visualização padrão (nós geográficos + pontos de registro)"
              onClick={() => setModoVisualizacao('padrao')}
              style={modoToggleBtnStyle(modoVisualizacao === 'padrao')}
            >
              <Layers fontSize="small" />
            </button>
            <button
              type="button"
              title="Mapa de calor (densidade de registros)"
              onClick={() => setModoVisualizacao('calor')}
              style={modoToggleBtnStyle(modoVisualizacao === 'calor')}
            >
              <LocalFireDepartment fontSize="small" />
            </button>
          </div>

          <button className={styles.blueBtn} onClick={() => window.history.back()} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px', alignItems: 'flex-start' }}>
        <select value={filtroProjeto} onChange={(e) => setFiltroProjeto(e.target.value)} style={filterInputStyle}>
          <option value="">Todos os projetos</option>
          {projetos.map((p) => <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>)}
        </select>

        <input type="date" value={filtroDataDe} onChange={(e) => setFiltroDataDe(e.target.value)} title="Data início (de)" style={filterInputStyle} />
        <input type="date" value={filtroDataAte} onChange={(e) => setFiltroDataAte(e.target.value)} title="Data início (até)" style={filterInputStyle} />

        <div style={{ position: 'relative', minWidth: '220px' }}>
          <input
            style={filterInputStyle}
            placeholder="Filtrar por local (bacia/rio/represa/área)..."
            value={localSelecionado ? localSelecionado.nome : buscaLocal}
            onChange={(e) => buscarLocal(e.target.value)}
          />
          {resultadosLocal.length > 0 && !localSelecionado && (
            <div style={suggestionBoxStyle}>
              {resultadosLocal.map((l) => (
                <div key={l.uid_pesca_local} style={suggestionItemStyle} onClick={() => { setLocalSelecionado(l); setResultadosLocal([]); }}>
                  <strong>{l.nome}</strong>{' '}
                  <span style={{ color: '#999', fontSize: '0.78rem' }}>{l.caminho || `(${ROTULO_TIPO[l.tipo] || l.tipo})`}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ position: 'relative', minWidth: '220px' }}>
          <input
            style={filterInputStyle}
            placeholder="Filtrar por município..."
            value={municipioSelecionado ? municipioSelecionado.municipio : buscaMunicipio}
            onChange={(e) => buscarMunicipio(e.target.value)}
          />
          {resultadosMunicipio.length > 0 && !municipioSelecionado && (
            <div style={suggestionBoxStyle}>
              {resultadosMunicipio.map((m) => (
                <div key={m.id_municipio} style={suggestionItemStyle} onClick={() => { setMunicipioSelecionado(m); setResultadosMunicipio([]); }}>
                  {m.municipio} {m.id_estado ? `- ${m.id_estado}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>

        {qtdFiltrosAtivos > 0 && (
          <button onClick={limparFiltros} style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer' }}>
            Limpar filtros
          </button>
        )}
      </div>

      {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 480px', height: '68vh', minHeight: '420px', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
          <MapContainer center={CENTRO_PADRAO} zoom={7} style={{ width: '100%', height: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {nos.map((no) => {
              const geom = no.geometria;
              if (!geom) return null;
              const tipoNo = CORES_TIPO[no.tipo] ? no.tipo : 'outro';
              if (!camadasTipos.includes(tipoNo)) return null;
              const cor = CORES_TIPO[tipoNo];

              if (geom.type === 'Polygon') {
                const anel = geom.coordinates?.[0];
                if (!Array.isArray(anel)) return null;
                return (
                  <Polygon key={no.uid_pesca_local} positions={anel.map(paraLatLng)} pathOptions={{ color: cor, weight: 2, fillOpacity: 0.15 }}>
                    <Popup>{no.nome} <span style={{ color: '#888' }}>({no.tipo})</span></Popup>
                  </Polygon>
                );
              }
              if (geom.type === 'LineString') {
                if (!Array.isArray(geom.coordinates)) return null;
                return (
                  <Polyline key={no.uid_pesca_local} positions={geom.coordinates.map(paraLatLng)} pathOptions={{ color: cor, weight: 3 }}>
                    <Popup>{no.nome} <span style={{ color: '#888' }}>({no.tipo})</span></Popup>
                  </Polyline>
                );
              }
              if (geom.type === 'Point') {
                const [lat, lng] = paraLatLng(geom.coordinates);
                return (
                  <CircleMarker key={no.uid_pesca_local} center={[lat, lng]} radius={5} pathOptions={{ color: cor, weight: 2, fillColor: cor, fillOpacity: 0.6 }}>
                    <Popup>{no.nome} <span style={{ color: '#888' }}>({no.tipo})</span></Popup>
                  </CircleMarker>
                );
              }
              return null;
            })}

            {/* Os nós geográficos (bacias/rios/represas) ficam de contexto
                nos dois modos — só a representação dos REGISTROS (o
                "dado" que varia em densidade) troca entre pontos e calor. */}
            {modoVisualizacao === 'padrao' ? (
              registrosVisiveis.map((r) => {
                const exato = r.precisao_local === 'exato';
                return (
                  <CircleMarker
                    key={r.uid_pesca_registro}
                    center={[r.lat, r.lng]}
                    radius={exato ? 7 : 6}
                    pathOptions={
                      exato
                        ? { color: '#00b37e', weight: 2, fillColor: '#00b37e', fillOpacity: 0.9 }
                        : { color: '#e08a00', weight: 2, dashArray: '3,3', fillColor: '#e08a00', fillOpacity: 0.35 }
                    }
                  >
                    <Popup>
                      <strong>{r.nome_local || 'Local não identificado'}</strong><br />
                      {new Date(r.data_inicio).toLocaleDateString('pt-BR')}<br />
                      Precisão: {exato ? 'exata (geolocalização)' : 'aproximada (rio + município)'}
                    </Popup>
                  </CircleMarker>
                );
              })
            ) : (
              <HeatmapLayer pontos={registrosVisiveis.map((r) => ({ lat: r.lat, lng: r.lng }))} />
            )}
          </MapContainer>
        </div>

        <div style={{ width: '220px', background: '#fff', borderRadius: '10px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', margin: '0 0 10px' }}>Legenda</p>

          <p style={{ fontSize: '0.78rem', color: '#666', margin: '0 0 6px', fontWeight: 600 }}>Registros de pescaria</p>
          {modoVisualizacao === 'padrao' ? (
            Object.entries(ROTULO_PRECISAO).map(([chave, rotulo]) => {
              const ativa = camadasPrecisao.includes(chave);
              const exato = chave === 'exato';
              return (
                <label key={chave} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', cursor: 'pointer', opacity: ativa ? 1 : 0.45 }}>
                  <input type="checkbox" checked={ativa} onChange={() => alternarCamada(setCamadasPrecisao, chave)} />
                  <span style={exato
                    ? { width: 12, height: 12, borderRadius: '50%', background: '#00b37e', display: 'inline-block' }
                    : { width: 12, height: 12, borderRadius: '50%', border: '2px dashed #e08a00', background: 'rgba(224,138,0,0.35)', display: 'inline-block' }} />
                  <span style={{ fontSize: '0.78rem' }}>{rotulo}</span>
                </label>
              );
            })
          ) : (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ height: 10, borderRadius: '6px', background: 'linear-gradient(to right, #3b82f6, #22c55e, #eab308, #ef4444)', marginBottom: '4px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#999' }}>
                <span>Menor densidade</span>
                <span>Maior densidade</span>
              </div>
            </div>
          )}

          <p style={{ fontSize: '0.78rem', color: '#666', margin: '12px 0 6px', fontWeight: 600 }}>Nós da árvore</p>
          {Object.entries(CORES_TIPO).map(([tipo, cor]) => {
            const ativa = camadasTipos.includes(tipo);
            return (
              <label key={tipo} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', cursor: 'pointer', opacity: ativa ? 1 : 0.45 }}>
                <input type="checkbox" checked={ativa} onChange={() => alternarCamada(setCamadasTipos, tipo)} />
                <span style={{ width: 12, height: 3, background: cor, display: 'inline-block' }} />
                <span style={{ fontSize: '0.75rem' }}>{ROTULO_TIPO[tipo] || tipo}</span>
              </label>
            );
          })}

          {carregando && <p style={{ fontSize: '0.78rem', color: '#999', marginTop: '10px' }}>Carregando...</p>}
        </div>
      </div>
    </div>
  );
};

const modoToggleBtnStyle = (ativo: boolean): React.CSSProperties => ({
  width: '32px', height: '32px', borderRadius: '50%', border: 'none', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: ativo ? '#03c9fe' : 'transparent', color: ativo ? '#fff' : '#666',
});
const filterInputStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', outline: 'none', fontFamily: 'inherit',
};
const suggestionBoxStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff',
  border: '1px solid #ddd', borderRadius: '8px', marginTop: '4px', maxHeight: '180px', overflowY: 'auto',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
};
const suggestionItemStyle: React.CSSProperties = {
  padding: '8px 10px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0',
};

export default MapaLocais;
